/**
 * Module 00 — Global Shell · Create Department modal (shared)
 *
 * Used by the onboarding gate (and reusable elsewhere) to create the user's
 * first department through identity `/departments`. On success the new
 * department is appended to the scope store and activated (addDepartment), so
 * the onboarding gate advances to the project step without a bootstrap reload.
 */

'use client';

import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/custom';
import { departmentsService } from '@/lib/api/services/departments';
import { useScopeStore } from '@/lib/stores/scope-store';

export function CreateDepartmentModal({
  open,
  onOpenChange,
  orgId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId?: string;
  onCreated?: (department: { id: string; name: string }) => void;
}) {
  const qc = useQueryClient();
  const addDepartment = useScopeStore((s) => s.addDepartment);
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) { setName(''); setError(null); }
  }, [open]);

  const submit = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    if (!orgId) { setError('No organization in scope'); return; }
    setSaving(true);
    try {
      const created = await departmentsService.create({ orgId, name: name.trim() });
      const department = { id: String(created.id), name: created.name };
      addDepartment(department); // activate so the gate advances to the project step
      qc.invalidateQueries({ queryKey: ['scope', 'departments'] });
      qc.invalidateQueries({ queryKey: ['settings', 'departments', orgId] });
      qc.invalidateQueries({ queryKey: ['session', 'bootstrap'] }); // refresh org/dept in sidebar
      toast.success(`Department created — ${department.name}`);
      onCreated?.(department);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.response?.status === 409 ? 'A department with that name already exists' : 'Failed to create department');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New department</DialogTitle></DialogHeader>
        <div className="py-2">
          <label className="mb-1.5 block text-[11px] font-semibold text-cs-text-muted">Name</label>
          <Input name="name" value={name} data-testid="onboarding-department-name"
            onChange={(e) => setName(e.target.value)} placeholder="e.g. Finance Ops"
            aria-invalid={!!error} autoFocus onKeyDown={(e) => e.key === 'Enter' && submit()} />
          {error && <p role="alert" className="mt-1 text-[12px] text-cs-error-400">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="primary" data-testid="onboarding-department-submit" loading={saving} onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
